import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Check } from "lucide-react";
import { toast } from "sonner";

const PLAN_DISPLAY = [
  {
    role: "Housing Seekers",
    intro: "Always free. Always.",
    plans: [
      { id: "free_seeker", name: "Free Forever", price: "$0", desc: "Search, intake, applications, alerts.", features: ["Unlimited search", "Multi-step intake", "Save favorite listings", "Submit applications", "Real-time vacancy alerts", "Verified Applicant program"], cta: "Create Free Account" },
    ],
  },
  {
    role: "Housing Providers",
    intro: "Empty beds cost you money — fill them faster.",
    plans: [
      { id: "provider_registration", name: "Provider Registration", price: "$9.99", note: "one-time", desc: "Get listed and verified.", features: ["Listing creation", "Admin verification queue", "Real-time vacancy posting"] },
      { id: "provider_standard", name: "Standard Provider", price: "$19.99", note: "/month", desc: "Standard visibility & referrals.", features: ["Unlimited listings", "Receive referrals", "Application tracking", "In-app + email notifications"] },
      { id: "provider_preferred", name: "Preferred Provider", price: "$49.99", note: "/month", highlight: true, desc: "Top placement, verified badge, priority referrals.", features: ["Everything in Standard", "Featured placement", "Verified Provider badge", "Priority referrals", "Vacancy alerts dashboard", "Enhanced profile"] },
    ],
  },
  {
    role: "Case Managers & Agencies",
    intro: "Stop spending hours making calls.",
    plans: [
      { id: "referral_partner_pro", name: "Professional", price: "$19.99", note: "/month", highlight: true, desc: "Full pro dashboard for independent case managers.", features: ["Save client profiles", "Submit & track referrals", "Referral history", "Vacancy alerts", "Applicant matching", "Placement tracking"] },
      { id: "agency_team", name: "Agency Team", price: "$99", note: "/month", desc: "Multi-user accounts for agencies.", features: ["Everything in Professional", "Unlimited team members", "Agency reporting", "Priority support", "Enterprise contracts"] },
    ],
  },
];

const ADD_ONS = [
  { id: "vacancy_alert", name: "Vacancy Blast", price: "$4.99", desc: "Push a single vacancy to all matching case managers and seekers." },
  { id: "verified_applicant", name: "Verified Applicant Package", price: "$49", desc: "ID, income, veteran status, and eligibility review for one placement." },
  { id: "placement_service", name: "Successful Placement Fee", price: "$199", desc: "Active placement: review, verify, coordinate intake. Only pay on move-in." },
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  const checkout = async (plan_id) => {
    if (!user) { navigate("/login"); return; }
    setBusy(plan_id);
    try {
      const { data } = await api.post("/checkout/session", { plan_id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create checkout");
      setBusy(null);
    }
  };

  return (
    <div data-testid="pricing-page" className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-3">Pricing</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#0A192F] tracking-tight">Free for seekers. Fair for everyone else.</h1>
        <p className="mt-5 text-[#4A5568] text-lg">Safe Harbor Veterans never charges the people who need housing. Revenue comes from providers who fill beds and agencies that move clients.</p>
      </div>

      {PLAN_DISPLAY.map((group) => (
        <section key={group.role} className="mb-16">
          <div className="border-b border-[#D1D5DB] pb-3 mb-8 flex items-end justify-between flex-wrap gap-2">
            <div className="font-heading text-xl font-semibold text-[#0A192F]">{group.role}</div>
            <div className="text-sm text-[#4A5568] italic">{group.intro}</div>
          </div>
          <div className={`grid grid-cols-1 md:grid-cols-${Math.min(3, group.plans.length)} gap-6`}>
            {group.plans.map((p) => (
              <div key={p.id} data-testid={`plan-${p.id}`} className={`bg-white border ${p.highlight ? "border-[#1E3A8A] border-2 relative" : "border-[#D1D5DB]"} rounded-sm p-7`}>
                {p.highlight && <div className="absolute -top-3 left-7 bg-[#1E3A8A] text-white text-[10px] tracking-widest uppercase font-bold px-3 py-1">Most popular</div>}
                <div className="font-heading text-lg font-semibold text-[#0A192F]">{p.name}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-heading text-4xl font-bold text-[#0A192F]">{p.price}</span>
                  {p.note && <span className="text-sm text-[#4A5568]">{p.note}</span>}
                </div>
                <div className="text-sm text-[#4A5568] mt-2">{p.desc}</div>
                <ul className="mt-5 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#0A192F]">
                      <Check className="w-4 h-4 text-[#276749] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {p.id === "free_seeker" ? (
                    <Button onClick={() => navigate("/register?role=seeker")} variant="outline" className="w-full border-[#0A192F] text-[#0A192F]">{p.cta || "Sign up"}</Button>
                  ) : (
                    <Button data-testid={`subscribe-${p.id}`} onClick={() => checkout(p.id)} disabled={busy === p.id} className={`w-full ${p.highlight ? "bg-[#1E3A8A] hover:bg-[#1A365D]" : "bg-[#0A192F] hover:bg-[#1A365D]"} text-white`}>
                      {busy === p.id ? "Redirecting…" : "Subscribe"}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="mt-8">
        <div className="font-heading text-xl font-semibold text-[#0A192F] border-b border-[#D1D5DB] pb-3 mb-8">Premium Add-Ons (one-time)</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {ADD_ONS.map((a) => (
            <div key={a.id} data-testid={`addon-${a.id}`} className="bg-white border border-[#D1D5DB] p-6 rounded-sm">
              <div className="font-heading font-semibold text-[#0A192F]">{a.name}</div>
              <div className="font-heading text-3xl font-bold text-[#C05621] mt-2">{a.price}</div>
              <div className="text-sm text-[#4A5568] mt-2">{a.desc}</div>
              <Button data-testid={`buy-${a.id}`} onClick={() => checkout(a.id)} disabled={busy === a.id} className="mt-5 w-full bg-[#C05621] hover:bg-[#9C4221] text-white">
                {busy === a.id ? "Redirecting…" : "Purchase"}
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-16 bg-[#0A192F] text-white rounded-sm p-10">
        <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-3">Future revenue (built-in)</div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold">Architected for placement fees, background screening, and compliance.</h2>
        <p className="mt-3 text-white/70 max-w-2xl">The platform is built to support placement fees ($150–$500), background screening, drug testing referrals, fingerprinting, and compliance services as the network grows.</p>
      </section>
    </div>
  );
}
